import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Check if user has access to onboarding walkthrough based on their Onboarding Key
 * @param {string} userId - The user's ID
 * @returns {Promise<boolean>} - True if user has access, false otherwise
 */
export const hasOnboardingAccess = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return false;
    }

    const userData = userDoc.data();
    
    // Check if user has onboarding key (id: 23) with count > 0
    if (userData.keys && userData.keys[23] && userData.keys[23] > 0) {
      return true;
    }

    // Check if user has any master keys that would grant access
    const masterKeys = [1, 3, 13, 14, 15, 19]; // Master keys that might grant onboarding access
    for (const keyId of masterKeys) {
      if (userData.keys && userData.keys[keyId] && userData.keys[keyId] > 0) {
        return true;
      }
    }

    return false;
  } catch (error) {
    console.error('Error checking onboarding access:', error);
    return false;
  }
};

/**
 * Consume an Onboarding Key for a user
 * @param {string} userId - The user's ID
 * @returns {Promise<boolean>} - True if key was consumed successfully, false otherwise
 */
export const consumeOnboardingKey = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return false;
    }

    const userData = userDoc.data();
    
    // Check if user has onboarding key available
    if (!userData.keys || !userData.keys[23] || userData.keys[23] <= 0) {
      return false;
    }

    // Consume the key
    const updatedKeys = {
      ...userData.keys,
      23: userData.keys[23] - 1
    };

    await updateDoc(doc(db, 'users', userId), {
      keys: updatedKeys
    });

    return true;
  } catch (error) {
    console.error('Error consuming onboarding key:', error);
    return false;
  }
};

/**
 * Grant an Onboarding Key to a user
 * @param {string} userId - The user's ID
 * @param {number} count - Number of keys to grant (default: 1)
 * @returns {Promise<boolean>} - True if key was granted successfully, false otherwise
 */
export const grantOnboardingKey = async (userId, count = 1) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return false;
    }

    const userData = userDoc.data();
    
    // Initialize keys object if it doesn't exist
    const currentKeys = userData.keys || {};
    const currentOnboardingKeys = currentKeys[23] || 0;
    
    const updatedKeys = {
      ...currentKeys,
      23: currentOnboardingKeys + count
    };

    await updateDoc(doc(db, 'users', userId), {
      keys: updatedKeys
    });

    return true;
  } catch (error) {
    console.error('Error granting onboarding key:', error);
    return false;
  }
};

/**
 * Get user's current key inventory
 * @param {string} userId - The user's ID
 * @returns {Promise<Object>} - Object containing user's keys
 */
export const getUserKeys = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return {};
    }

    const userData = userDoc.data();
    return userData.keys || {};
  } catch (error) {
    console.error('Error getting user keys:', error);
    return {};
  }
};

/**
 * Start onboarding walkthrough for a user
 * @param {string} userId - The user's ID
 * @returns {Promise<boolean>} - True if onboarding was started successfully, false otherwise
 */
export const startOnboardingWalkthrough = async (userId) => {
  try {
    // Check if user has access
    const hasAccess = await hasOnboardingAccess(userId);
    if (!hasAccess) {
      return false;
    }

    // Consume the onboarding key
    const keyConsumed = await consumeOnboardingKey(userId);
    if (!keyConsumed) {
      return false;
    }

    // Update user's onboarding status
    await updateDoc(doc(db, 'users', userId), {
      onboardingStatus: {
        completed: false,
        currentStep: 'existing-conditions',
        steps: {
          'existing-conditions': {
            completed: false,
            required: true
          },
          'nda': {
            completed: false,
            required: true
          },
          'profile-completion': {
            completed: false,
            required: true
          }
        }
      },
      status: 'onboarding'
    });

    return true;
  } catch (error) {
    console.error('Error starting onboarding walkthrough:', error);
    return false;
  }
}; 